import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  DataTable,
  DataTableCellVariants,
  EmptyState,
  Highlight,
  Icon,
  Pagination,
  RowsSkeletonLoader,
  useSidePanel,
  type DataTableHeaderProps,
  type DataTableRowData,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useGetPaymentLimits } from 'hooks/queries/useGetPaymentLimits'
import { useSearchablePagination } from 'hooks/useSearchablePagination'
import { EditLimitSheet } from 'pages/PaymentLimits/components/EditLimitSheet'
import { formatPaymentMethodLabel, LIMIT_TYPE_LABELS } from 'pages/PaymentLimits/utils'
import type { Limit } from 'types/paymentLimits'
import { formatNumberWithCommas } from 'utils/utils'
import mockCustomerPaymentLimits from '../../../../__mocks__/PaymentLimits/customerPaymentLimits.json'
import { CustomerPaymentLimitSidePanel } from './CustomerPaymentLimitSidePanel'

interface CustomerPaymentLimitsTableProps {
  customerReference: string
}

export const CustomerPaymentLimitsTable = ({
  customerReference,
}: CustomerPaymentLimitsTableProps) => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useSearchablePagination(MIN_PAGINATION_SIZE)

  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false)
  const [selectedLimit, setSelectedLimit] = useState<Limit | null>(null)

  const { data: limitsData, isFetching } = useGetPaymentLimits({
    accountId: currentAccount?.reference,
    customerId: customerReference,
    page,
    size: rowsPerPage,
  })

  const limits = limitsData?.content ?? (mockCustomerPaymentLimits?.content as Limit[])
  const totalElements = limitsData?.totalElements ?? mockCustomerPaymentLimits?.totalElements ?? 0
  const hasLimits = limits.length > 0

  const {
    isSidePanelOpen,
    sidePanelData: selectedSidePanelLimit,
    paging: sidePanelPaging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(limits)

  const openSidePanel = (index: number) => {
    updateSidePanel(index)
    setIsSidePanelOpen(true)
  }

  const handleEditLimit = (limit: Limit) => {
    setSelectedLimit(limit)
    setIsEditSheetOpen(true)
  }

  const handleCloseSheet = () => {
    setIsEditSheetOpen(false)
    setSelectedLimit(null)
  }

  const columns: DataTableHeaderProps[] = useMemo(
    () => [
      {
        key: 'paymentMethod',
        label: t('customers.paymentLimitsTable.paymentMethod'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'currency',
        label: t('customers.paymentLimitsTable.currency'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'limitType',
        label: t('customers.paymentLimitsTable.limitType'),
        variant: DataTableCellVariants.HIGHLIGHT,
      },
      {
        key: 'amount',
        label: t('customers.paymentLimitsTable.amount'),
        variant: DataTableCellVariants.TEXT,
        className: 'text-right',
      },
      {
        key: 'actions',
        label: '',
        variant: DataTableCellVariants.ACTION,
      },
    ],
    [t]
  )

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!limits?.length) return []

    return limits.map((limit, index) => ({
      id: limit.ruleId,
      testid: `payment-limit-row-${limit.ruleId}`,
      onRowClick: () => openSidePanel(index),
      cells: [
        {
          key: 'paymentMethod',
          variant: DataTableCellVariants.TEXT,
          Text: formatPaymentMethodLabel(limit.qualifiers.method),
        },
        {
          key: 'currency',
          variant: DataTableCellVariants.TEXT,
          Text: limit.currency,
        },
        {
          key: 'limitType',
          variant: DataTableCellVariants.HIGHLIGHT,
          Highlights: [
            <Highlight key={`${limit.ruleId}-limitType`}>
              {t(LIMIT_TYPE_LABELS[limit.limitType] ?? limit.limitType)}
            </Highlight>,
          ],
        },
        {
          key: 'amount',
          variant: DataTableCellVariants.TEXT,
          Text: formatNumberWithCommas(limit.amount),
          className: 'text-right',
        },
        {
          key: 'actions',
          variant: DataTableCellVariants.ACTION,
          Actions: [
            <Button
              key={`edit-${limit.ruleId}`}
              variant="ghost"
              noPadding
              iconElement={<Icon name="SimplePencilCustom" />}
              onClick={e => {
                e.stopPropagation()
                handleEditLimit(limit)
              }}
              testId={`edit-limit-${limit.ruleId}`}
            />,
          ],
        },
      ],
    }))
  }, [limits])

  if (isFetching) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (!hasLimits) {
    return (
      <EmptyState
        testId="customer-payment-limits-empty-state"
        LottiePlayer={<Lottie src="no-data" />}
        title={t('customers.paymentLimitsTable.emptyStateTitle')}
        description={t('customers.paymentLimitsTable.emptyStateDescription')}
      />
    )
  }

  return (
    <>
      <DataTable name="CustomerPaymentLimitsTable" headers={columns} data={tableData} />

      {totalElements > rowsPerPage && (
        <Box paddingT={16}>
          <Pagination
            testId="customer-payment-limits-pagination"
            page={page}
            rowsPerPage={rowsPerPage}
            total={totalElements}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      )}

      <EditLimitSheet isOpen={isEditSheetOpen} limit={selectedLimit} onClose={handleCloseSheet} />

      <CustomerPaymentLimitSidePanel
        isOpen={isSidePanelOpen}
        limit={selectedSidePanelLimit}
        paging={sidePanelPaging}
        onClose={() => setIsSidePanelOpen(false)}
      />
    </>
  )
}
